import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Permission } from '@/api';
import Checkbox from '@/design_system/Checkbox';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import { Role, useUpdateRole } from '@/models/role';

import { PERMISSION_DESCRIPTIONS, PERMISSION_LABELS } from './permission-labels';
import { SCOPE_KEYS, ScopeKey, useRoleScopeLabels } from './scope-labels';

export const RoleForm = ({ role }: { role: Role }) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const scopeLabels = useRoleScopeLabels();
  const { mutateAsync: updateRole, isPending } = useUpdateRole();

  const [description, setDescription] = useState(role.description ?? '');

  useEffect(() => {
    setDescription(role.description ?? '');
  }, [role]);

  const showUpdateSuccess = (roleName: string) => {
    showToast({
      text: t({
        id: 'settings.roles.update.success',
        message: `Role "${roleName}" updated successfully`,
      }),
      type: 'success',
    });
  };

  const showUpdateError = () => {
    showToast({
      text: t({
        id: 'settings.roles.create.error.unknown',
        message: 'Unknown error. Please contact Support',
      }),
      type: 'error',
    });
  };

  const handleDescriptionBlur = () => {
    const nextDescription = description.trim() || null;

    if (nextDescription === role.description) {
      return;
    }

    updateRole({
      roleId: role.id,
      description: nextDescription,
    })
      .then(() => {
        showUpdateSuccess(role.name);
      })
      .catch(() => {
        setDescription(role.description ?? '');
        showUpdateError();
      });
  };

  const handleScopeChange = (key: ScopeKey, checked: boolean) => {
    const nextScopes = {
      scopeOrganization: role.scopeOrganization,
      scopeStore: role.scopeStore,
      scopeWorkshop: role.scopeWorkshop,
      scopeAfterSales: role.scopeAfterSales,
      [key]: checked,
    };

    const hasAtLeastOneScope = SCOPE_KEYS.some((scopeKey) => nextScopes[scopeKey]);

    if (!hasAtLeastOneScope) {
      showToast({
        text: t({
          id: 'settings.roles.create.error.no-scope',
          message: 'At least one scope must be selected',
        }),
        type: 'error',
      });

      return;
    }

    updateRole({
      roleId: role.id,
      ...nextScopes,
    })
      .then(() => {
        showUpdateSuccess(role.name);
      })
      .catch(showUpdateError);
  };

  const handlePermissionToggle = (permission: Permission) => {
    const nextPermissions = role.permissions.includes(permission)
      ? role.permissions.filter((p) => p !== permission)
      : [...role.permissions, permission];

    updateRole({
      roleId: role.id,
      permissions: nextPermissions,
    })
      .then(() => {
        showUpdateSuccess(role.name);
      })
      .catch(showUpdateError);
  };

  return (
    <Stack gap="24px">
      <InputText
        label={<Trans id="settings.roles.create.field.description.label">Description</Trans>}
        placeholder={t({
          id: 'settings.roles.create.field.description.placeholder',
          message: 'Describe the role purpose',
        })}
        value={description}
        onChange={setDescription}
        onBlur={handleDescriptionBlur}
        isDisabled={isPending}
      />
      <Stack gap="8px">
        <InlineText weight="medium">
          <Trans id="settings.roles.create.field.scopes.label">Scopes</Trans>
        </InlineText>
        <Stack row gap="16px" flexWrap="wrap">
          {SCOPE_KEYS.map((key) => (
            <Checkbox
              key={key}
              isSelected={role[key]}
              onChange={(checked) => handleScopeChange(key, checked)}
              isDisabled={isPending}
            >
              {scopeLabels[key]}
            </Checkbox>
          ))}
        </Stack>
      </Stack>
      <Stack gap="8px">
        <InlineText weight="medium">
          <Trans id="settings.roles.create.field.permissions.label">Permissions</Trans>
        </InlineText>
        <Stack gap="16px">
          {(Object.keys(PERMISSION_LABELS) as Permission[])
            .map((permission) => ({
              key: permission,
              label: t(PERMISSION_LABELS[permission]),
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ key, label }) => (
              <Stack key={key} gap="2px">
                <Checkbox
                  isSelected={role.permissions.includes(key)}
                  onChange={() => handlePermissionToggle(key)}
                  isDisabled={isPending}
                >
                  {label}
                </Checkbox>
                <Paragraph size="200" color="secondary" style={{ paddingLeft: '28px' }}>
                  {t(PERMISSION_DESCRIPTIONS[key])}
                </Paragraph>
              </Stack>
            ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
